import ExcelJS from 'exceljs';
import type { Column } from '@devexpress/dx-react-grid';

export const MAX_PREVIEW_ROWS = 500;

export interface ParsedSpreadsheet {
  columns: Column[];
  rows: Record<string, string>[];
  totalRows: number;
  truncated: boolean;
  /** Sheets in the workbook beyond the first, not rendered (see spec §5). */
  extraSheetCount: number;
}

/** Split a raw delimited-text string into rows of cells, honoring RFC 4180
 *  quoting: quoted fields may contain the delimiter and newlines, and `""`
 *  inside a quoted field is an escaped literal quote. `delimiter` is a
 *  single character — `,` for CSV, `\t` for TSV (PR feedback on OMNI-697:
 *  `.tsv` files were being parsed with a hardcoded comma, so a tab-separated
 *  file rendered as one column per row). Hand-rolled rather than a
 *  dependency — this grammar is small enough to fully specify and test
 *  directly, and it keeps one more third-party parser off a
 *  security-sensitive, user-uploaded-file code path (see OMNI-697 Task 3
 *  revision note on why `xlsx`/SheetJS was rejected for the XLSX half of
 *  this file). */
function parseDelimitedRows(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  const endField = () => {
    row.push(field);
    field = '';
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };

  while (i < text.length) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += char;
      i += 1;
      continue;
    }
    if (char === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (char === delimiter) {
      endField();
      i += 1;
      continue;
    }
    if (char === '\r') {
      i += 1;
      continue;
    }
    if (char === '\n') {
      endRow();
      i += 1;
      continue;
    }
    field += char;
    i += 1;
  }
  if (field !== '' || row.length > 0) endRow();
  return rows.filter((r) => !(r.length === 1 && r[0] === ''));
}

/** Render an `exceljs` cell value as display text. Most cells are plain
 *  primitives; formulas/hyperlinks/rich text are objects that need
 *  unwrapping (see `ExcelJS.CellValue`). Exported for direct unit testing --
 *  covering it via a real XLSX round-trip would require `workbook.xlsx.load`,
 *  which this test environment can't run (unrelated Blob/Buffer mismatch). */
export function cellToText(value: ExcelJS.CellValue): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object') {
    if ('richText' in value)
      return value.richText.map((segment) => segment.text).join('');
    if ('text' in value) return value.text;
    if ('result' in value) {
      const result = value.result;
      if (result === null || result === undefined) return '';
      // ExcelJS types CellFormulaValue.result's error branch as
      // `{ error: CellErrorValue }` -- a nested object, not the flat
      // `CellErrorValue` the top-level CellValue union expects -- so this
      // can't recurse through cellToText directly; unwrap it here instead.
      if (typeof result === 'object' && 'error' in result)
        return String(result.error);
      return cellToText(result);
    }
    return '';
  }
  return String(value);
}

function toGrid(rows: string[][]): { header: string[]; body: string[][] } {
  if (rows.length === 0) return { header: [], body: [] };
  const [headerRow, ...bodyRows] = rows;
  const header = headerRow.map((cell, i) => cell.trim() || `Column ${i + 1}`);
  const body = bodyRows.map((r) => header.map((_, i) => r[i] ?? ''));
  return { header, body };
}

function buildResult(
  header: string[],
  body: string[][],
  extraSheetCount: number
): ParsedSpreadsheet {
  const columns: Column[] = header.map((title, i) => ({
    name: `col_${i}`,
    title,
  }));
  const truncated = body.length > MAX_PREVIEW_ROWS;
  const rows = (truncated ? body.slice(0, MAX_PREVIEW_ROWS) : body).map(
    (cells) => {
      const row: Record<string, string> = {};
      cells.forEach((value, i) => {
        row[`col_${i}`] = value;
      });
      return row;
    }
  );
  return { columns, rows, totalRows: body.length, truncated, extraSheetCount };
}

async function parseDelimited(
  blob: Blob,
  delimiter: string
): Promise<ParsedSpreadsheet> {
  const text = await blob.text();
  const { header, body } = toGrid(parseDelimitedRows(text, delimiter));
  return buildResult(header, body, 0);
}

async function parseXlsx(blob: Blob): Promise<ParsedSpreadsheet> {
  const buffer = await blob.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  // exceljs's TS types declare `load` as accepting a Node `Buffer`, but at
  // runtime it accepts the raw `ArrayBuffer` too — that's how it's used in a
  // browser bundle, where no real `Buffer` exists (the cast is type-level
  // only).
  //
  // exceljs feeds the input to `StreamBuf.write`, which dispatches purely on
  // `instanceof` and accepts only String / Buffer / ArrayBuffer / StringBuf.
  // Two consequences, both verified against a real .xlsx in plain node and
  // in jest:
  //
  //   1. A `Uint8Array` matches NONE of those branches and throws "Chunk must
  //      be one of type String, Buffer or StringBuf." Wrapping the buffer was
  //      previously believed to fix a jsdom realm mismatch; it is in fact the
  //      one input shape exceljs refuses, and it made every XLSX preview fail
  //      to parse and fall back to the download card.
  //   2. `instanceof ArrayBuffer` is realm-sensitive, so `blob.arrayBuffer()`
  //      is not enough on its own: under jsdom the Blob comes from a separate
  //      realm and its ArrayBuffer fails the check even though it holds valid
  //      bytes.
  //
  // Copying into an ArrayBuffer allocated here satisfies both — the copy is
  // made with this module's own `ArrayBuffer`, so the `instanceof` holds in
  // every environment. One extra copy of the file bytes, xlsx-only.
  const bytes = new Uint8Array(buffer);
  const localBuffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(localBuffer).set(bytes);
  await workbook.xlsx.load(
    localBuffer as unknown as Parameters<typeof workbook.xlsx.load>[0]
  );

  const sheet = workbook.worksheets[0];
  if (!sheet) {
    return {
      columns: [],
      rows: [],
      totalRows: 0,
      truncated: false,
      extraSheetCount: 0,
    };
  }

  const rawRows: string[][] = [];
  sheet.eachRow((row) => {
    // exceljs's `row.values` is 1-indexed (index 0 is always undefined,
    // since spreadsheet columns are 1-based) — documented exceljs behavior.
    const values = row.values as ExcelJS.CellValue[];
    rawRows.push(values.slice(1).map(cellToText));
  });

  const { header, body } = toGrid(rawRows);
  return buildResult(header, body, workbook.worksheets.length - 1);
}

/** Parse a CSV, TSV, or XLSX blob into a `{columns, rows}` shape `DataGrid`
 *  can render. `kind` is required rather than auto-detected: the caller
 *  already knows it from `resolveFilePreviewKind`. CSV/TSV are hand-parsed
 *  (see `parseDelimitedRows`); XLSX uses `exceljs`. Renders only the first
 *  sheet of a multi-sheet workbook (`extraSheetCount` reports how many more
 *  exist) and caps rows at `MAX_PREVIEW_ROWS`. */
export async function parseSpreadsheet(
  blob: Blob,
  kind: 'csv' | 'tsv' | 'xlsx'
): Promise<ParsedSpreadsheet> {
  if (kind === 'csv') return parseDelimited(blob, ',');
  if (kind === 'tsv') return parseDelimited(blob, '\t');
  return parseXlsx(blob);
}
